import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useI18n } from "@/i18n";
import { useAuth } from "@/context/AuthContext";
import { AppLayout } from "@/components/AppLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Settings as SettingsIcon, ShieldCheck, Building2, ExternalLink, Phone } from "lucide-react";

export default function Settings() {
  const { t } = useI18n();
  const { user, updateUser } = useAuth();
  const [states, setStates] = useState([]);
  const [regulator, setRegulator] = useState(null);
  const [form, setForm] = useState({
    name: user?.name || "", state: user?.state || "", zip_code: user?.zip_code || "",
    household_type: user?.household_type || "", dependents: user?.dependents ?? "",
    homeowner_status: user?.homeowner_status || "", emergency_savings: user?.emergency_savings ?? "",
  });

  useEffect(() => { api.get("/states").then((r) => setStates(r.data)); }, []);
  useEffect(() => { if (form.state) api.get(`/regulator/${form.state}`).then((r) => setRegulator(r.data)).catch(() => setRegulator(null)); }, [form.state]);

  const save = async () => {
    try {
      const payload = { ...form,
        dependents: form.dependents === "" ? null : parseInt(form.dependents),
        emergency_savings: form.emergency_savings === "" ? null : parseFloat(form.emergency_savings) };
      const r = await api.put("/profile", payload);
      updateUser(r.data);
      toast.success(t("profile_saved"));
    } catch { toast.error(t("could_not_save")); }
  };

  return (
    <AppLayout>
      <h1 className="font-display text-3xl font-semibold text-navy flex items-center gap-2"><SettingsIcon className="h-7 w-7 text-brandblue" /> {t("settings")}</h1>

      <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 rounded-3xl bg-white border border-slate-100 card-shadow p-6">
          <h2 className="text-lg font-semibold text-navy">{t("settings_household")}</h2>
          <p className="text-sm text-slate-400">{t("settings_household_sub")}</p>
          <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div><Label>{t("name")}</Label><Input data-testid="set-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1 rounded-xl" /></div>
            <div><Label>{t("state")}</Label>
              <Select value={form.state} onValueChange={(v) => setForm({ ...form, state: v })}>
                <SelectTrigger data-testid="set-state" className="mt-1 rounded-xl"><SelectValue placeholder={t("ob_select")} /></SelectTrigger>
                <SelectContent>{states.map((s) => <SelectItem key={s.code} value={s.code}>{s.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>{t("zip")}</Label><Input value={form.zip_code} onChange={(e) => setForm({ ...form, zip_code: e.target.value })} className="mt-1 rounded-xl" /></div>
            <div><Label>{t("household")}</Label>
              <Select value={form.household_type} onValueChange={(v) => setForm({ ...form, household_type: v })}>
                <SelectTrigger className="mt-1 rounded-xl"><SelectValue placeholder={t("ob_select")} /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="single">{t("hh_single")}</SelectItem><SelectItem value="married">{t("hh_married")}</SelectItem>
                  <SelectItem value="family">{t("hh_family")}</SelectItem><SelectItem value="retired">{t("hh_retired")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><Label>{t("dependents")}</Label><Input data-testid="set-dependents" type="number" value={form.dependents} onChange={(e) => setForm({ ...form, dependents: e.target.value })} className="mt-1 rounded-xl" /></div>
            <div><Label>{t("homeowner_status")}</Label>
              <Select value={form.homeowner_status} onValueChange={(v) => setForm({ ...form, homeowner_status: v })}>
                <SelectTrigger className="mt-1 rounded-xl"><SelectValue placeholder={t("ob_select")} /></SelectTrigger>
                <SelectContent><SelectItem value="own">{t("own")}</SelectItem><SelectItem value="rent">{t("rent")}</SelectItem></SelectContent>
              </Select>
            </div>
            <div className="sm:col-span-2"><Label>{t("emergency_savings")}</Label><Input data-testid="set-savings" type="number" value={form.emergency_savings} onChange={(e) => setForm({ ...form, emergency_savings: e.target.value })} className="mt-1 rounded-xl" /></div>
          </div>
          <Button data-testid="save-profile-btn" onClick={save} className="mt-5 rounded-full brand-gradient text-white">{t("save")}</Button>
        </div>

        <div className="space-y-6">
          {regulator && (
            <div className="rounded-3xl bg-white border border-slate-100 card-shadow p-6">
              <h2 className="text-sm font-semibold text-navy flex items-center gap-2"><Building2 className="h-4 w-4 text-brandblue" /> {t("your_regulator")}</h2>
              <p className="mt-2 font-medium text-navy">{regulator.name}</p>
              <a href={regulator.website} target="_blank" rel="noreferrer" className="mt-2 inline-flex items-center gap-1.5 text-sm text-brandblue"><ExternalLink className="h-4 w-4" />{regulator.website}</a>
              <p className="mt-2 flex items-center gap-1.5 text-sm text-slate-500"><Phone className="h-4 w-4" />{regulator.phone}</p>
            </div>
          )}
          <div className="rounded-3xl bg-teal-50/60 border border-teal-100 p-6">
            <h2 className="text-sm font-semibold text-navy flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-brandteal" /> {t("your_data_privacy")}</h2>
            <p className="mt-2 text-sm text-slate-500">{t("data_privacy_note")}</p>
          </div>
          <div className="rounded-3xl bg-white border border-slate-100 card-shadow p-6 text-sm text-slate-500">
            <p><span className="font-medium text-navy">{t("account")}:</span> {user?.email}</p>
            <p className="mt-1"><span className="font-medium text-navy">{t("sign_in_method")}:</span> {user?.auth_provider === "google" ? t("google") : t("email_password")}</p>
          </div>
        </div>
      </div>
    </AppLayout>
  );
}
